import /*React,*/ {
  //createContext,
  useCallback,
  //useContext,
  useMemo,
  //useState,
  //Fragment,

  // Types
  ElementType,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
} from 'react'

import { Props } from '../../types'
import { render } from '../../utils/render'
import { useId } from '../../hooks/use-id'
import { Keys } from '../keyboard'
import { resolvePropValue } from '../../utils/resolve-prop-value'
import { isDisabledReactIssue7711 } from '../../utils/bugs'


let DEFAULT_BUTTON_TAG = 'button' as const
interface ButtonRenderPropArg {
  target: "_self" | "_blank"
}
type ButtonPropsWeControl =
  | 'id'
  | 'role'
  | 'tabIndex'
  | 'target'
  | 'onClick'
  | 'onKeyUp'
  | 'onKeyPress'

export function Button<TTag extends ElementType = typeof DEFAULT_BUTTON_TAG>(
  props: Props<
    TTag,
    ButtonRenderPropArg,
    ButtonPropsWeControl | 'target' | 'className'
  > & {
    // Special treatment, can either be a string or a function that resolves to a string
    className?: ((bag: ButtonRenderPropArg) => string) | string
  }
) {
  let { target, className, ...passThroughProps } = props
  let id = `headlessui-button-${useId()}`

  let handleClick = useCallback(
    (event: ReactMouseEvent) => {
      if (isDisabledReactIssue7711(event.currentTarget)) return event.preventDefault()
      event.preventDefault()
    },
    []
  )
  let handleKeyUp = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      if (event.key !== Keys.Tab) event.preventDefault()
    },
    []
  )

  // This is needed so that we can "cancel" the click event when we use the `Enter` key on a button.
  let handleKeyPress = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => event.preventDefault(),
    []
  )

  let propsBag = useMemo<ButtonRenderPropArg>(() => ({ target }), [target])
  let propsWeControl = {
    id,
    role: 'button',
    tabIndex: 0,
    className: resolvePropValue(className, propsBag),
    target: target,
    'aria-label': id,
    onClick: handleClick,
    onKeyUp: handleKeyUp,
    onKeyPress: handleKeyPress,
  }

  if (passThroughProps.as === 'button') {
    Object.assign(propsWeControl, { type: 'button' })
  }

  return render({ ...passThroughProps, ...propsWeControl }, propsBag, DEFAULT_BUTTON_TAG)
}